import React, { useCallback, useMemo, useRef, useState } from 'react';
import styles from './NotificationDetail.module.scss';
import Icon from '../../../../components/Icon/Icon';
import { useMessageContext } from '../../../../context/MessageContext';
import { useTranslation } from 'react-i18next';
import VideoPlayer from '../../../../routes/newVideo/VideoPlayer/VideoPlayer';
import { CustomButtonTooltip } from '../../../../components/ButtonTooltip/ButtonTooltip';
import MiniUserDetail from '../../../../components/MiniUserDetail/MiniUserDetail';
import { getAvatarFromMessage } from '../../utils';
import { CustomButton } from '../../../../components/Button/Button';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { RecordingOnWebModal } from '../../../../components/RecordingOnWebModal/RecordingOnWebModal';
import DOMPurify from 'dompurify';
import { isStringContainsHtml } from '../../../../utils/stringHelper';
import useAccessibleClick from '../../../../../common/hooks/useAccessibleClick';

const NotificationDetail = () => {
  const { messageDetails, deleteMessage } = useMessageContext();
  const { t } = useTranslation();

  const avatar = useMemo(() => getAvatarFromMessage(messageDetails), [messageDetails]);

  const location = useLocation();
  const history = useHistory();

  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);

  const [showOnWebModal, setShowOnWebModal] = useState(false);

  const handleDelete = useCallback(() => {
    deleteMessage(messageDetails, true);
    handleBackToList();
  }, [handleBackToList, deleteMessage, messageDetails]);

  const deleteRef = useAccessibleClick(handleDelete);

  const cleanBody = useMemo(() => {
    if (isStringContainsHtml(messageDetails.body)) {
      return DOMPurify.sanitize(messageDetails.body, { USE_PROFILES: { html: true, svg: true }, FORBID_TAGS: ['script'] });
    }
    return messageDetails.body;
  }, [messageDetails]);

  const getDateFormat = () => {
    const currentDate = new Date();
    const currentDateHours = currentDate.getHours();
    const currentDateInMilliseconds = currentDate.getTime();
    const difference = currentDateInMilliseconds - messageDetails.publishedAt;

    var minutes = Math.floor(difference / 1000 / 60);
    var hours = Math.floor(minutes / 60);
    var days = Math.floor(hours / 24);
    var weeks = Math.floor(days / 7);
    var month = Math.floor(days / 30);
    var year = Math.floor(days / 360);

    if (year >= 1) {
      if (year === 1) {
        return `${t('notification.time.year', { date: year })}`;
      } else {
        return `${t('notification.time.years', { date: year })}`;
      }
    } else if (month >= 1) {
      if (month === 1) {
        return `${t('notification.time.month', { date: month })}`;
      } else {
        return `${t('notification.time.months', { date: month })}`;
      }
    } else if (weeks >= 1) {
      if (weeks === 1) {
        return `${t('notification.time.week', { date: weeks })}`;
      } else {
        return `${t('notification.time.weeks', { date: weeks })}`;
      }
    } else if (days >= 2) {
      return `${t('notification.time.days', { date: days })}`;
    } else if (hours - currentDateHours <= 24 && days >= 1) {
      // if it was yesterday but more than 24 hours ago
      return `${t('notification.time.yesterday')}`;
    } else if (hours >= 1) {
      if (hours === 1) {
        return `${t('notification.time.hour', { date: hours })}`;
      } else {
        return `${t('notification.time.hours', { date: hours })}`;
      }
    } else if (minutes >= 1) {
      return `${t('notification.time.minutes', { date: minutes })}`;
    } else {
      return `${t('notification.time.now')}`;
    }
  };

  const handleBackToList = () => {
    const queryParams = new URLSearchParams(location.search);
    const type = queryParams.get('type');
    history.push({
      pathname: location.pathname,
      ...(type ? { search: 'type=release_announcement' } : {}),
    });
  };

  const handleClick = useCallback(() => {
    let containsProtocol = messageDetails.cta.action.includes('http://') || messageDetails.cta.action.includes('https://');
    if (isMobileView && messageDetails.cta.action.includes('edit')) {
      setShowOnWebModal(true);
      return;
    }
    window.open(`${containsProtocol ? '' : 'https://'}${messageDetails.cta.action}`);
  }, [isMobileView]);

  if (Object.keys(messageDetails).length === 0) {
    return null;
  }

  return (
    <div className={styles.notificationDetail}>
      <div className={styles.header}>
        <div className={styles.title}>
          <CustomButton onClick={handleBackToList} variant="link" className={styles.goBackButton} ariaLabel={t('general.back')}>
            <Icon src="back" className={styles.backIcon} onClick={handleBackToList} />
          </CustomButton>
          <p tabIndex={0}>
            <span onClick={handleBackToList}>{t('notification.notifications')}</span> / {t('notification.message')}
          </p>
        </div>
      </div>
      <div className={styles.details}>
        <div className={styles.infoContainer}>
          <div className={styles.userInfo}>
            <MiniUserDetail
              avatar={avatar}
              name={messageDetails.sender}
              size="large"
              classNames={{ avatarClassName: styles.userAvatar, titleClassName: styles.userName }}
            />
          </div>
          <div className={styles.time} tabIndex={0}>
            <p>{getDateFormat(messageDetails)}</p>
          </div>
        </div>
        <div className={styles.titleContainer}>
          <div tabIndex={0} className={styles.messageTitle}>{messageDetails.subject}</div>
          <div ref={deleteRef} className={styles.trashIcon} onClick={handleDelete} tabIndex={0} aria-label={t('general.delete')}>
            <Icon src="trash" verticalAlign="middle" style={{ fontSize: '24px' }} />
          </div>
        </div>
        <div tabIndex={0} className={styles.messageContent} dangerouslySetInnerHTML={{ __html: cleanBody }} />
        {messageDetails.video && (
          <div className={styles.player}>
            <VideoPlayer overlay originalURL={messageDetails.video} />
          </div>
        )}
        {messageDetails.cta && (
          <div className={styles.callToActionContainer}>
            <CustomButtonTooltip
              label={messageDetails.cta.text}
              variant={'primary'}
              onClick={handleClick}
              customStyles={{ marginTop: '24px', justifyContent: 'center' }}
            />
          </div>
        )}
      </div>
      <RecordingOnWebModal show={showOnWebModal} onClose={() => setShowOnWebModal(false)} />
    </div>
  );
};

export default NotificationDetail;
